<template>
  <div class="settings">
    <LeftPane compact @is-sidebar-resized="resizeTopBar" />
    <BaseTopBar
      v-if="isMounted"
      :panel="panel"
      :left-panel-width="leftPaneWidth"
      :is-navigation-displayed="false"
    >
      <template #left>
        <Breadcrumb :items="breadcrumbItems" :show-workspace-icon="false" />
      </template>
      <template v-if="topbarActions.onSave || topbarActions.shareRoot" #right>
        <template v-if="topbarActions.onSave">
          <BaseButton
            kind="main"
            type="primary"
            text="Save"
            :state="topbarActions.saveState ?? undefined"
            @click="topbarActions.onSave?.()"
          />
          <BaseButton
            kind="main"
            type="secondary"
            text="Run"
            :state="topbarActions.runState ?? undefined"
            @click="topbarActions.onRun?.()"
          >
            <template #icon>
              <StartIcon />
            </template>
          </BaseButton>
        </template>
        <ShareButton
          v-if="topbarActions.shareRoot"
          :root="topbarActions.shareRoot"
          :organization-id="topbarActions.shareOrganizationId"
        />
      </template>
      <template v-if="topbarActions.moreActions.length > 0" #more>
        <BaseButton
          v-for="action in topbarActions.moreActions"
          :key="action.text"
          class="context-action"
          kind="auxiliary"
          :text="action.text"
          :tint="action.tint ?? 'none'"
          is-expanded
          @click="action.onClick()"
        >
          <template v-if="action.icon" #icon>
            <component :is="action.icon" />
          </template>
          <template v-if="action.shortcutsDisplay" #right>
            {{ action.shortcutsDisplay }}
          </template>
        </BaseButton>
      </template>
    </BaseTopBar>
    <div class="wrapper">
      <RouterView v-slot="{ Component }">
        <component :is="Component" />
      </RouterView>
    </div>
    <Teleport to="#modal">
      <NotionAddAccount
        :is-displayed="addNotionAccount"
        :token="notionInfos?.access_token"
        @close="() => (addNotionAccount = false)"
      />
    </Teleport>
  </div>
</template>

<script lang="ts">
import {
  computed,
  ComputedRef,
  defineComponent,
  onMounted,
  Ref,
  ref,
  watch,
} from "vue";
import { useRoute } from "vue-router";

import { NotionIntegration } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import { Panel } from "@/components/base/types";
import Breadcrumb, { BreadcrumbItem } from "@/components/Breadcrumb.vue";
import StartIcon from "@/components/icons/StartIcon.vue";
import * as inject from "@/components/inject";
import NotionAddAccount from "@/components/NotionAddAccount.vue";
import LeftPane from "@/components/settings/LeftPane.vue";
import { provideSettingsTopbarActions } from "@/components/settings/topbarActions";
import ShareButton from "@/components/ShareButton.vue";
import { getLastAccessedOrganization } from "@/composables/useOrganization";
import { RouteNames } from "@/routes/types";
import { firstRouteParam, navigateTo } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseButton,
    BaseTopBar,
    Breadcrumb,
    LeftPane,
    NotionAddAccount,
    ShareButton,
    StartIcon,
  },
  async setup() {
    const route = useRoute();
    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const integrations = inject.integrations();
    // The current route's organization view of the integrations store, used
    // to resolve the `:appKey` route param into an integration name for the
    // breadcrumb of the generic integration sub-pages.
    const routeOrganizationIntegrations = integrations.forOrganization(
      computed(() => firstRouteParam(route.params.orgId))
    );
    const addNotionAccount = ref(false);
    const notionInfos: Ref<NotionIntegration | null> = ref(null);
    const panel = Panel.leftPanel;
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));
    const isRoute = (name: RouteNames) => route?.name === name;
    const breadcrumbItems: ComputedRef<BreadcrumbItem[]> = computed(() => {
      const orgId = firstRouteParam(route.params.orgId) ?? "";
      const integrationsCrumb = {
        title: "Integrations",
        link: {
          name: RouteNames.SettingsIntegrations,
          params: { orgId },
          query: {},
        },
      };
      // This only works because the path is known from the leaf information.
      if (
        isRoute(RouteNames.SettingsIntegrationsNotion) ||
        isRoute(RouteNames.SettingsIntegrationsGmail) ||
        isRoute(RouteNames.SettingsIntegrationsGmailConsent) ||
        isRoute(RouteNames.SettingsIntegrationsGoogleWorkspace) ||
        isRoute(RouteNames.SettingsIntegrationsGoogleCalendar) ||
        isRoute(RouteNames.SettingsIntegrationsGoogleContacts)
      ) {
        return [integrationsCrumb];
      } else if (isRoute(RouteNames.SettingsIntegrationsNotionSetup)) {
        return [
          integrationsCrumb,
          {
            title: "Notion",
            link: {
              name: RouteNames.SettingsIntegrationsNotion,
              params: { orgId },
              query: {},
            },
          },
        ];
      } else if (
        isRoute(RouteNames.SettingsMembraneIntegration) ||
        isRoute(RouteNames.SettingsMcpIntegration)
      ) {
        return [integrationsCrumb];
      } else if (isRoute(RouteNames.SettingsMembraneAddDataCollection)) {
        const appKey = firstRouteParam(route.params.appKey) ?? "";
        const app = routeOrganizationIntegrations.getServiceByKey(appKey);
        return [
          integrationsCrumb,
          {
            title: app?.name || "Integration",
            link: {
              name: RouteNames.SettingsMembraneIntegration,
              params: { orgId, appKey },
              query: {},
            },
          },
        ];
      } else if (isRoute(RouteNames.SettingsTypeEdition)) {
        return [
          {
            title: "Types",
            link: {
              name: RouteNames.SettingsTypes,
              params: {
                orgId,
                workspaceId: firstRouteParam(route.params.workspaceId) ?? "",
              },
              query: {},
            },
          },
        ];
      } else if (isRoute(RouteNames.SettingsWorkspace)) {
        return [
          {
            title: "Workspaces",
            link: {
              name: RouteNames.SettingsWorkspaces,
              params: { orgId },
              query: {},
            },
          },
        ];
      } else if (isRoute(RouteNames.SettingsAutomation)) {
        return [
          {
            title: "Automations",
            link: {
              name: RouteNames.SettingsAutomations,
              params: { orgId },
              query: {},
            },
          },
        ];
      }
      return [];
    });
    watch(
      () => route?.query.notion_code,
      async (notionCode) => {
        if (notionCode && typeof notionCode === "string") {
          notionInfos.value =
            await controller.integration.notion.exchangeToken(notionCode);
          addNotionAccount.value = true;
        }
        history.replaceState(null, "Settings", route?.path);
        // Navigate to the org-scoped Notion integrations page: prefer the
        // route's organization, then the persisted last-accessed one, then
        // the personal organization.
        const routeOrgId = route.params.orgId;
        const orgId =
          (typeof routeOrgId === "string" && routeOrgId) ||
          getLastAccessedOrganization(profile.value) ||
          profile.value.user.personal.organizationId;
        navigateTo(RouteNames.SettingsIntegrationsNotion, router, {
          params: { orgId },
        });
      }
    );
    const topbarActions = provideSettingsTopbarActions();

    const leftPaneWidth = ref(180);
    const resizeTopBar = (width: number) => (leftPaneWidth.value = width);

    return {
      addNotionAccount,
      breadcrumbItems,
      topbarActions,
      isMounted,
      leftPaneWidth,
      notionInfos,
      panel,
      resizeTopBar,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/context-actions.scss";

.settings {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;

  .wrapper {
    width: 100%;
    padding: 52px 76px 52px 76px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 700px;
    margin: 0 auto;
    overflow-y: scroll;
    scrollbar-width: none;
  }
}
</style>
